import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Brain,
  MessageCircle,
  Target,
  Users,
  Clock,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  Play,
  BookOpen,
} from "lucide-react";
import { useLanguage } from "../../utils/LanguageContext";
import content from "../../content/methodPage.json"

const MethodPage = () => {
 
  const { language, setLanguage } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const t = content[language];

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Enhanced 3D Sound Wave Logo
  const SoundWaveLogo = () => {
    return (
      <div className="w-10 h-10 bg-gradient-to-r from-[#0B1F3B] to-[#0F172A] rounded-lg flex items-center justify-center relative overflow-hidden shadow-lg">
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="11" width="2" height="2" rx="1" fill="currentColor" />
          <rect x="5.5" y="9" width="2" height="6" rx="1" fill="currentColor" />
          <rect x="9" y="7" width="2" height="10" rx="1" fill="currentColor" />
          <rect
            x="12.5"
            y="4"
            width="2"
            height="16"
            rx="1"
            fill="currentColor"
          />
          <rect x="16" y="8" width="2" height="8" rx="1" fill="currentColor" />
          <rect
            x="19.5"
            y="10"
            width="2"
            height="4"
            rx="1"
            fill="currentColor"
          />
        </svg>
      </div>
    );
  };

  // Language Toggle Component
  const LanguageToggle = () => (
    <div className="flex items-center space-x-2  bg-[#011f4b] rounded-full p-1 w-[100px] sm:w-[100px]">
      <button
        onClick={() => setLanguage("es")}
        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
          language === "es"
            ? "bg-lexora-accent text-white shadow-lg"
            : "text-white hover:text-white"
        }`}
      >
        ES
      </button>
      <button
        onClick={() => setLanguage("en")}
        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
          language === "en"
            ? "bg-lexora-accent text-white shadow-lg"
            : "text-white hover:text-white"
        }`}
      >
        EN
      </button>
    </div>
  );


  return (
    <div className="min-h-screen bg-lexora-dark">
      {/* <Header /> */}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-[#0D2642] overflow-hidden">
        <img class="absolute inset-0 w-full h-full object-cover" alt="background" src="assests\images\final-inner-bg.png"></img>
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 left-10 w-32 h-32 bg-lexora-accent/20 rounded-full blur-3xl"
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl"
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
          />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:pt-20 md:pt-20 lg:pt-20 pt-20 pb-0 sm:pb-0 sm:pb-0 lg:pb-0 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="leading-tight xl:leading-tight text-4xl sm:text-5xl md:text-5xl lg:text-5xl font-heading-custom font-bold text-white leading-heading tracking-heading mb-6"
          >
            <span className="block">{t.hero.title}</span>
            <span className="block gradient-text">{t.hero.subtitle}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-white mb-8 leading-relaxed max-w-3xl mx-auto"
          >
            {t.hero.description}
          </motion.p>
        </div>
      </section>

      {/* Method Pillars Section */}
      <section className="py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-10 md:mb-10 lg:mb-12 xl:mb-16 2xl:mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading-custom font-bold text-[#333] tracking-heading leading-heading mb-3 sm:mb-3 md:mb-3 lg:mb-6 xl:mb-8 2xl:mb-8">
              {t.pillars.title}
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="space-y-12"
          >
            {t.pillars.items.map((pillar, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`flex flex-col lg:flex-row items-center gap-8 sm:gap-8 md:gap-8 lg:gap-12 xl:gap-12 2xl:gap-12 ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className="flex-1 w-full sm:w-full md:w-full md:max-w-[727px]">
                  <div className="bg-[#0D2642] rounded-2xl p-8">
                    <div className="feature-icon text-[#fff] mb-6">
                      <div className="bg-gradient-to-r from-[#4A7CFF] to-[#3B82F6] inline-flex justify-center items-center rounded-full w-16 h-16">
                        {index === 0 && <BookOpen className="w-10 h-10" />}
                        {index === 1 && <MessageCircle className="w-10 h-10" />}
                        {index === 2 && <Users className="w-10 h-10" />}
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-white mb-4">
                      {pillar.title}
                    </h3>
                    <p className="text-white mb-1 sm:mb-2 md:mb-2 lg:mb-4 xl:mb-4 2xl:mb-6">{pillar.description}</p>
                    <p className="text-white">{pillar.details}</p>
                  </div>
                </div>
                <div className="flex-1 w-full sm:w-full md:w-full md:max-w-[727px]">
                  <div className="relative bg-gradient-to-br from-lexora-accent/20 to-purple-600/20 rounded-2xl">
                    <div className="w-full h-64 bg-gradient-to-br from-lexora-accent/20 to-purple-600/20 rounded-2xl flex items-center justify-center">
                      <div className="text-8xl text-white">
                        {index === 0 && "📚"}
                        {index === 1 && "🗣️"}
                        {index === 2 && "👥"}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-[radial-gradient(circle_at_85%_15%,rgba(90,140,255,0.12)_0%,rgba(90,140,255,0)_40%),radial-gradient(circle_at_10%_90%,rgba(120,170,255,0.10)_0%,rgba(120,170,255,0)_45%),linear-gradient(180deg,#0D1F3A_0%,#0B2240_40%,#081C36_100%)]">
        <div className="overlay-service bg-[url('../public/assests/images/lexora-bg.png')] bg-cover bg-center  bg-no-repeat py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading-custom font-bold text-white mb-8">
              {t.process.title}
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {t.process.steps.map((step, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="light-card rounded-2xl p-8 text-center relative"
              >
                <div className="bg-gradient-to-r from-[#4A7CFF] to-[#3B82F6] absolute -top-4 left-1/2 transform -translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {step.number}
                </div>
                <div className=" color-[#0F172A] mb-6 flex justify-center mt-4">
                  {index === 0 && <Target className="w-10 h-10" />}
                  {index === 1 && <Brain className="w-10 h-10" />}
                  {index === 2 && <Play className="w-10 h-10" />}
                  {index === 3 && <CheckCircle className="w-10 h-10" />}
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-4">
                  {step.title}
                </h3>
                <p className="text-[#64748B]">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-0 sm:py-5 md:py-5 lg:py-5 xl:py-6 2xl:py-6">
        <div className="py-10 2xl:py-14 xl:py-14 max-w-7xl mx-auto px-4 sm:px-4 md:px-4 lg:px-8 text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <SoundWaveLogo />
            <span className="text-2xl font-heading font-bold text-gray-800 tracking-heading">
              Lexora
            </span>
          </div>
          <p className="text-gray-800 mb-8">
            {language === "es"
              ? "Transformando carreras a través de habilidades excepcionales de comunicación en inglés."
              : "Transforming careers through exceptional English communication skills."}
          </p>
          <div className="flex justify-center space-x-3 sm:space-x-4 md:space-x-4 lg:space-x-6 xl:space-x-6 2xl:space-x-6">
            <a
              href="/"
              className="text-gray-800 hover:text-[#4A7CFF] font-semibold transition-colors whitespace-nowrap font-body"
            >
              Home
            </a>
            <a
              href="/method"
              className="text-gray-800 hover:text-[#4A7CFF] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.method}
            </a>
            <a
              href="/instructors"
              className="text-gray-800 hover:text-[#4A7CFF] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.instructors}
            </a>
            <a
              href="/success"
              className="text-gray-800 hover:text-[#4A7CFF] font-semibold transition-colors whitespace-nowrap font-body"
            >
              {t.nav.success}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MethodPage;
